import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { PartnerDrawer, type PartnerRow } from '../shared/PartnerDrawer';

interface Payload { rows: PartnerRow[]; companies: { id: number; code: string; name: string }[] }

export function AdminPartnersSection({ token, locale, reloadTick }: { token: string; locale: 'hr' | 'en'; reloadTick: number }) {
    const def = adminSectionDefinition('112');
    const title = def ? adminSectionNavLabel(def, locale) : '112';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [search, setSearch] = useState('');
    const [archived, setArchived] = useState(false);
    const [editing, setEditing] = useState<PartnerRow | 'new' | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const p = new URLSearchParams();
            if (companyFilter) p.set('company_id', companyFilter);
            if (search.trim()) p.set('q', search.trim());
            if (archived) p.set('archived', '1');
            setData(await api<Payload>(`/admin/partners?${p.toString()}`, token));
        } catch (err) { setError(err instanceof Error ? err.message : 'API'); } finally { setLoading(false); }
    }, [token, companyFilter, search, archived]);
    useEffect(() => { void load(); }, [load, reloadTick]);

    return (
        <section className="card">
            <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>🏢 {title}</h3>
                <span className="muted" style={{ fontSize: '0.84rem' }}>{t('Dobavljači — naziv, OIB, IBAN, kontakt, firme', 'Suppliers — name, OIB, IBAN, contact, companies')}</span>
                <button className="compact" onClick={() => setEditing('new')} style={{ marginLeft: 'auto', background: 'var(--ad-acc)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600 }}>+ {t('Novi partner', 'New partner')}</button>
            </header>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} style={inp}>
                    <option value="">{t('Sve firme', 'All companies')}</option>
                    {data?.companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                </select>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Traži naziv / OIB / IBAN / kontakt', 'Search name / OIB / IBAN / contact')} style={{ ...inp, minWidth: 280 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.84rem' }}>
                    <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> {t('Arhiva', 'Archived')}
                </label>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {!loading && data ? (
                <div className="table-wrap">
                    <table className="admin-user-table">
                        <thead>
                            <tr>
                                <th>{t('Naziv', 'Name')}</th><th>OIB</th><th>IBAN</th>
                                <th>{t('Kontakt', 'Contact')}</th><th>{t('Firme', 'Companies')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.length === 0 ? <tr><td colSpan={5} className="muted" style={{ padding: 14 }}>{t('Nema partnera.', 'No partners.')}</td></tr> : data.rows.map((p) => (
                                <tr key={p.id} className="interactive-row" style={{ cursor: 'pointer', opacity: p.archived ? 0.55 : 1 }} onClick={() => setEditing(p)} title={t('Otvori', 'Open')}>
                                    <td><strong>{p.name}</strong>{p.archived ? <span className="muted"> · {t('arhiva', 'archived')}</span> : null}</td>
                                    <td style={{ color: p.oib ? undefined : '#c0392b' }}>{p.oib ?? t('— fali —', '— missing —')}</td>
                                    <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: p.iban ? undefined : '#c0392b' }}>{p.iban ?? t('— fali —', '— missing —')}</td>
                                    <td>{p.contact_name ?? '—'}</td>
                                    <td>{p.companies.map((c) => c.code).join(', ') || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {editing ? (
                <PartnerDrawer
                    token={token}
                    locale={locale}
                    partner={editing === 'new' ? null : editing}
                    companies={data?.companies ?? []}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); void load(); }}
                    setError={setError}
                />
            ) : null}
        </section>
    );
}

const inp: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem' };
